// Archived Alerts — audit trail for the Morning Reset workflow. Every
// archived MAVIS recommendation stays searchable here and can be restored.
import { useEffect, useState } from "react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, Undo2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { listArchivedRecommendations, restoreRecommendation } from "@/lib/executive/engine";

const CATEGORIES = [
  { value: "", label: "All categories" },
  { value: "stale", label: "Stale" },
  { value: "test", label: "Test data" },
  { value: "expired", label: "Expired" },
  { value: "duplicate", label: "Duplicate" },
  { value: "cancelled_registration", label: "Cancelled registration" },
  { value: "manual", label: "Manual" },
];

export default function ArchivedAlertsPage() {
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listArchivedRecommendations({
        limit: 100,
        category: category || undefined,
        search: search || undefined,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (e: any) {
      toast({ title: "Failed", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);

  const restore = async (id: string) => {
    setRestoringId(id);
    try {
      await restoreRecommendation(id);
      toast({ title: "Restored", description: "Alert is back in the Executive Brief." });
      setItems((prev) => prev.filter((x) => x.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e: any) {
      toast({ title: "Failed", description: e.message, variant: "destructive" });
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <AdminShell section="archived_alerts">
      <div className="space-y-4 max-w-5xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold">Archived Alerts</h1>
            <p className="text-sm text-muted-foreground">
              History of every alert that was archived by the Morning Reset — searchable and restorable.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Refresh
          </Button>
        </div>

        <Card>
          <CardContent className="p-3 flex flex-wrap items-center gap-2">
            <Input
              value={search}
              placeholder="Search titles…"
              className="max-w-xs"
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
            />
            <select
              className="border rounded px-2 py-1 text-sm bg-background"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <Button size="sm" variant="outline" onClick={load}>Update Filters</Button>
            <div className="ml-auto text-xs text-muted-foreground">{total} archived</div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Looking through the archive…
          </div>
        ) : items.length === 0 ? (
          <div className="text-sm text-muted-foreground">Nothing archived yet.</div>
        ) : (
          <div className="space-y-2">
            {items.map((r) => (
              <Card key={r.id} className="p-3">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-sm">{r.title}</div>
                      <Badge variant="outline">{r.archive_category}</Badge>
                      <Badge variant="secondary">{r.priority}</Badge>
                    </div>
                    {r.summary && (
                      <div className="text-xs text-muted-foreground mt-0.5">{r.summary}</div>
                    )}
                    {r.archive_reason && (
                      <div className="text-xs text-muted-foreground mt-1">
                        <b>Archived because:</b> {r.archive_reason}
                      </div>
                    )}
                    {r.archived_at && (
                      <div className="text-[11px] text-muted-foreground mt-1">
                        Archived {new Date(r.archived_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => restore(r.id)} disabled={restoringId === r.id}>
                    {restoringId === r.id ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <Undo2 className="h-4 w-4 mr-1" />
                    )}
                    Restore
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminShell>
  );
}
